import React from 'react';
import PropTypes from 'prop-types';
import { between } from 'airbnb-prop-types';
import clsx from 'clsx';
import { ssColorCyan, ssColorWhite } from '../constants/style-constants';
import styleVars from '../design-system/scss-vars';

export const Tick = () => (
  <svg
    className="icon icon-tick"
    width="30"
    height="30"
    viewBox="0 0 30 30"
    version="1.1"
    xmlnsXlink="http://www.w3.org/1999/xlink"
  >
    <g
      className="icon-wrapper"
      transform="translate(3.000000, 6.000000)"
      stroke="#FFF"
      strokeWidth={2}
      fill="none"
      fillRule="evenodd"
      strokeLinecap="square"
    >
      <path d="M4.437 14.642L.13 9.542M23.87.131L6.39 17.649" />
    </g>
  </svg>
);

export const Cross = ({ className, ...props }) => (
  <svg
    className={clsx('icon', 'icon-cross', className)}
    width="30"
    height="30"
    viewBox="0 0 30 30"
    version="1.1"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    {...props}
  >
    <g
      className="icon-wrapper"
      transform="translate(4.793103, 3.800000)"
      stroke="#FFF"
      strokeWidth={2}
      fill="none"
      fillRule="evenodd"
      strokeLinecap="square"
    >
      <path d="M7.7 8.068L0 0m21.184 21.668L13.483 13.6M21.542 0L0 21.614" />
    </g>
  </svg>
);

Cross.defaultProps = {
  className: '',
};

Cross.propTypes = {
  className: PropTypes.string,
};

export const Slash = () => (
  <svg
    className="icon icon-cross"
    width="30"
    height="30"
    viewBox="0 0 30 30"
    version="1.1"
    xmlnsXlink="http://www.w3.org/1999/xlink"
  >
    <g
      className="icon-wrapper"
      transform="translate(4.793103, 3.800000)"
      stroke="#FFF"
      strokeWidth={2}
      fill="none"
      fillRule="evenodd"
      strokeLinecap="square"
    >
      <path d="m26.3351,3.8l-21.542,21.614" />
    </g>
  </svg>
);

export const Plus = props => (
  <svg
    width="19"
    height="19"
    viewBox="0 0 19 19"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    {...props}
  >
    <line x1="9.5" y1="0" x2="9.5" y2="19" strokeWidth="1" stroke="white" />
    <line x1="0" y1="9.5" x2="19" y2="9.5" strokeWidth="1" stroke="white" />
  </svg>
);

export const Chevron = ({
  className,
  color,
  rotate,
  width,
  height,
  ...props
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 30 30"
    version="1.1"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    className={`icon icon-chevron ${className}`}
    style={{ transform: `rotate(${rotate}deg)` }}
    {...props}
  >
    <g
      className="icon-wrapper"
      stroke={color}
      strokeWidth={2}
      transform="translate(8, 0)"
      fill="none"
      fillRule="evenodd"
      strokeLinecap="square"
    >
      <polyline points="1 0.94738775 15 14.9473878 1 28.9473878" />
    </g>
  </svg>
);

Chevron.defaultProps = {
  className: '',
  color: '#fff',
  rotate: 0,
  width: 30,
  height: 30,
};

Chevron.propTypes = {
  className: PropTypes.string,
  color: PropTypes.string,
  rotate: between({ gte: -360, lte: 360 }),
  width: PropTypes.number,
  height: PropTypes.number,
};

export const Play = ({
  className,
  stroke,
  strokeWidth,
  ...props
}) => (
  <svg
    width="30"
    height="30"
    viewBox="0 0 30 30"
    version="1.1"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    className={`icon icon-play ${className}`}
    {...props}
  >
    <g
      fill="none"
      fillRule="evenodd"
      stroke="none"
    >
      <g stroke={stroke} strokeWidth={strokeWidth}>
        <circle cx="15" cy="15" r="14" />
        <path d="M11.8471544 8.09715444L18.6937721 14.9437721 11.8471544 21.7903897" />
      </g>
    </g>
  </svg>
);

Play.defaultProps = {
  stroke: ssColorCyan,
  strokeWidth: '2',
  className: '',
};

Play.propTypes = {
  stroke: PropTypes.string,
  strokeWidth: PropTypes.string,
  className: PropTypes.string,
};

export const External = ({ stroke, strokeWidth, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="35"
    height="35"
    viewBox="0 0 35 35"
    {...props}
  >
    <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
      <g {...{ stroke, strokeWidth }} transform="translate(-7 -8)">
        <path
          d="M8.94149532 27.115964L8.94149532 38.865964 11.2914953 41.215964 30.0914953 41.215964 32.4414953 38.865964 32.4414953 20.065964 30.0914953 17.715964 23.75918 17.715964 18.6777989 17.715964"
          transform="rotate(90 20.691 29.466)"
        />
        <g transform="translate(21.114 1.926)">
          <path
            d="M20.7272395 21.0950291L11.2394894 11.607279 11.2394894 10.3950554 20.7272395 0.906447991"
            transform="rotate(135 15.983 11)"
          />
          <path
            d="M-2.875 17.095l27-.26"
            transform="rotate(135 10.625 16.965)"
          />
        </g>
      </g>
    </g>
  </svg>
);

External.defaultProps = {
  stroke: ssColorWhite,
  strokeWidth: '2',
};

External.propTypes = {
  stroke: PropTypes.string,
  strokeWidth: PropTypes.string,
};

export const OpenQuote = ({ color, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="108"
    height="95"
    viewBox="0 0 108 95"
    {...props}
  >
    <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="0">
      <path
        fill={color}
        fillRule="nonzero"
        d="M108 .423v16.064c-6.275 1.673-11.045 4.016-14.31 7.028-3.265 3.012-5.872 6.84-7.823 11.483-1.95 4.644-2.925 10.312-2.925 17.006 7.971 0 13.886 2.259 17.744 6.777 3.858 4.518 5.788 9.329 5.788 14.432 0 5.94-2.12 10.96-6.36 15.06s-9.201 6.15-14.883 6.15c-7.377 0-13.695-3.117-18.952-9.35-5.258-6.233-7.887-14.746-7.887-25.54 0-9.537 2.078-18.469 6.233-26.794 4.155-8.325 9.837-15.436 17.045-21.335C88.878 5.506 97.654 1.845 108 .423zm-58.52 0v16.064c-6.275 1.673-11.045 4.016-14.31 7.028-3.264 3.012-5.872 6.84-7.822 11.483-1.95 4.644-2.926 10.312-2.926 17.006 8.14 0 14.12 2.259 17.935 6.777 3.816 4.518 5.724 9.329 5.724 14.432 0 5.94-2.141 10.96-6.423 15.06-4.283 4.1-9.222 6.15-14.82 6.15-7.292 0-13.588-3.117-18.888-9.35C2.65 78.84 0 70.327 0 59.533c0-9.537 2.078-18.469 6.233-26.794 4.155-8.325 9.815-15.436 16.98-21.335C30.38 5.506 39.136 1.845 49.48.423z"
      />
    </g>
  </svg>
);

export default OpenQuote;

OpenQuote.defaultProps = {
  color: styleVars.ssBright,
};

OpenQuote.propTypes = {
  color: PropTypes.string,
};
